export const RETURN_REASON_LABELS: Record<string, string> = {
  DEFECTIVE: 'Producto defectuoso',
  WRONG_ITEM: 'Artículo incorrecto',
  NOT_AS_DESCRIBED: 'No coincide con la descripción',
  CUSTOMER_CHANGED_MIND: 'Cliente cambió de opinión',
  SIZE_ISSUE: 'Problema de talla',
  QUALITY_ISSUE: 'Problema de calidad',
  OTHER: 'Otro',
};

export interface ParsedReturnHistoryNotes {
  returnReason?: string;
  returnReasonLabel?: string;
  orderRefundAmount?: number;
  refundAmount?: number;
  restockingFee?: number;
  itemCondition?: string;
  isCustomAmount?: boolean;
  returnNotes?: string;
  isPartial?: boolean;
}

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export const formatReturnReasonLabel = (reason?: string): string | undefined => {
  if (!reason?.trim()) {
    return undefined;
  }
  return RETURN_REASON_LABELS[reason] ?? reason;
};

const parseLegacyReturnNotes = (notes: string): ParsedReturnHistoryNotes => {
  const result: ParsedReturnHistoryNotes = {
    isPartial: notes.toLowerCase().includes('devolución parcial'),
  };

  const reasonMatch = notes.match(/Motivo:\s*([^·]+)/i);
  if (reasonMatch?.[1]) {
    const returnReason = reasonMatch[1].trim();
    result.returnReason = returnReason;
    result.returnReasonLabel = formatReturnReasonLabel(returnReason);
  }

  const refundMatch = notes.match(/Reembolso:\s*([\d.]+)\s*Bs/i);
  if (refundMatch?.[1]) {
    const parsed = parseFloat(refundMatch[1]);
    if (Number.isFinite(parsed)) {
      result.orderRefundAmount = parsed;
    }
  }

  return result;
};

export const parseReturnHistoryNotes = (notes?: string): ParsedReturnHistoryNotes => {
  if (!notes?.trim()) {
    return {};
  }

  const trimmed = notes.trim();
  if (trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(trimmed) as Record<string, unknown>;
      const returnReason = typeof data.returnReason === 'string' ? data.returnReason : undefined;
      const orderRefundAmount = parseOptionalNumber(data.orderRefundAmount);
      const refundAmount = parseOptionalNumber(data.refundAmount);
      const restockingFee = parseOptionalNumber(data.restockingFee);
      const returnNotes = typeof data.returnNotes === 'string' ? data.returnNotes : undefined;
      const itemCondition = typeof data.itemCondition === 'string' ? data.itemCondition : undefined;

      return {
        returnReason,
        returnReasonLabel: formatReturnReasonLabel(returnReason),
        orderRefundAmount,
        refundAmount,
        restockingFee,
        itemCondition,
        isCustomAmount: data.isCustomAmount === true,
        returnNotes,
        isPartial: data.isPartial === true,
      };
    } catch {
      return parseLegacyReturnNotes(trimmed);
    }
  }

  return parseLegacyReturnNotes(trimmed);
};

/** Texto para mostrar en el historial: solo comentario escrito por el usuario. */
export const formatReturnHistoryDetail = (parsed: ParsedReturnHistoryNotes): string | undefined => {
  const notes = parsed.returnNotes?.trim();
  return notes || undefined;
};
